import { useEffect, useRef, useState } from 'react';
import { CelebrationPopup } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { resolveAuthTransition, type AuthTransition } from './resolve-auth-transition';

/** Duree d'affichage avant fermeture automatique (ms). */
const AUTO_DISMISS_MS = 2600;

/**
 * Overlay global qui celebre les transitions d'auth avec un popup anime facon
 * `FeedbackPopup` : « Bienvenue » a la connexion, « À bientôt » a la deconnexion.
 *
 * Monte a la racine (main.tsx), en frere du `RouterProvider`, pour survivre a la
 * redirection de route que la deconnexion declenche (session → null ⇒ retour a
 * `/login`) : le popup « au revoir » reste visible par-dessus l'ecran de login.
 *
 * S'abonne directement a `onAuthStateChange` (et non a `useAuth`) pour lire le
 * TYPE d'evenement — indispensable pour distinguer une vraie connexion d'une
 * restauration de session (cf. `resolveAuthTransition`).
 */
export function AuthTransitionPopup() {
  const { profile } = useAuth();
  const [transition, setTransition] = useState<AuthTransition | null>(null);
  // Presence d'une session AVANT l'evenement courant. supabase-js rejoue l'etat
  // initial (`INITIAL_SESSION`) au moment de l'abonnement → cette ref est amorcee
  // correctement avant qu'un eventuel `SIGNED_IN` de refresh n'arrive.
  const hadSessionRef = useRef(false);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const hasSession = !!session;
      const next = resolveAuthTransition(event, hadSessionRef.current, hasSession);
      hadSessionRef.current = hasSession;
      if (next) setTransition(next);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (!transition) return null;

  const name = profile?.display_name?.trim();
  const close = () => setTransition(null);

  if (transition === 'welcome') {
    return (
      <CelebrationPopup
        emoji="👋"
        headline={name ? `Salut ${name} !` : 'Bienvenue !'}
        message="Ravi de te revoir. C’est reparti !"
        variant="positive"
        autoDismissMs={AUTO_DISMISS_MS}
        onClose={close}
      />
    );
  }

  return (
    <CelebrationPopup
      emoji="👋"
      headline="À bientôt !"
      message="Tu es déconnecté·e. Reviens vite continuer ta progression."
      variant="neutral"
      autoDismissMs={AUTO_DISMISS_MS}
      onClose={close}
    />
  );
}
